import { useEffect, useEffectEvent, useRef, useState } from 'react';
import type { StaffingOperationsPort } from '../operations/port';
import type { StaffingPlan, StaffingPlanInput } from '../operations/planModel';
import { StaffingPlanDraftController, staffingPlanFingerprint } from './staffingPlanDraftController';

export type StaffingPlanSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';

export function useStaffingPlanDraft({ initial, input, port, fixtureMode, enabled = true, onPersisted }: {
  initial?: StaffingPlan; input: StaffingPlanInput; port: StaffingOperationsPort; fixtureMode: boolean;
  enabled?: boolean; onPersisted?: (plan: StaffingPlan) => void;
}) {
  const [saved, setSaved] = useState(initial);
  const [status, setStatus] = useState<StaffingPlanSaveStatus>(initial ? 'saved' : 'idle');
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const current = useRef({ input, enabled, onPersisted });
  current.current = { input, enabled, onPersisted };
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [controller] = useState(() => new StaffingPlanDraftController({ initial, port, fixtureMode, onPersisted: (plan) => {
    if (!mounted.current) return;
    setSaved(plan); current.current.onPersisted?.(plan);
  } }));
  const fingerprint = staffingPlanFingerprint(input);
  const dirty = !controller.isSaved(input);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; window.clearTimeout(timer.current); }; }, []);

  const save = async (retry = false) => {
    window.clearTimeout(timer.current);
    if (!current.current.enabled) throw new Error('발령안을 수정할 권한이 없습니다.');
    setStatus('saving'); setError('');
    try {
      const plan = await controller.persist(current.current.input, retry);
      if (mounted.current) setStatus(controller.isSaved(current.current.input) ? 'saved' : 'pending');
      return plan;
    } catch (cause) {
      if (mounted.current) {
        setStatus(controller.isConflict ? 'conflict' : 'error');
        setError(cause instanceof Error ? cause.message : '초안을 저장하지 못했습니다. 다시 시도해 주세요.');
      }
      throw cause;
    }
  };
  const autoSave = useEffectEvent(() => { void save().catch(() => undefined); });
  useEffect(() => {
    if (!enabled || !dirty || !input.title.trim() || !input.effectiveDate || status === 'error' || status === 'conflict' || status === 'saving') return;
    setStatus('pending');
    timer.current = window.setTimeout(autoSave, 1200);
    return () => window.clearTimeout(timer.current);
  }, [enabled, fingerprint, dirty, input.title, input.effectiveDate, status]);

  return { saved, status, error, dirty, flush: () => save(), retry: () => save(true) };
}
